import {
    Stack,
    Alert,
    Group,
    Select,
    UnstyledButton,
    ActionIcon,
    Tooltip,
} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useContext, useEffect, useRef, useState} from "react";
import {AppContext} from "../chat/AppContext";
import IconSidebar from "../../assets/sidebar.svg?react";
import {useTranslation} from "react-i18next";
import {Link, Outlet, useLocation} from "react-router-dom";
import {DashboardSearchSection} from "./DashboardSearchSection";
import type {PortalGrouping} from "../../dibAppApi/dibAppApi";
import {usePortalData} from "./usePortalData";
import {NavItem} from "../../components/NavItem";
import {ANDRE_SLUG, groupingSlug} from "./PortalsPage";
import IconChevronRight from "../../assets/chevron-right.svg?react";
import IconChevronDown from "../../assets/chevron-down.svg?react";
import classes from "./DashboardView.module.css";

type ActiveView = "portals" | "myWork" | "content" | "chatSearch";

// We honor an inbound hash (external deep-link) on a genuine fresh load — but
// not on internal back-navigation remounts. We no longer write the hash
// ourselves while scrolling or on sidebar clicks; the active section is tracked
// purely in React state. A module-scoped flag survives remounts but resets on
// reload.
let hasHonoredInboundHash = false;

// The dashboard scrolls inside an inner container (AppShell main / mainContent),
// not the window, so the browser's native scroll restoration never restores it.
// Remember each route's scroll position ourselves so back-navigation lands where
// the user left off. Module-scoped: survives remounts, resets on reload.
const scrollPositions = new Map<string, number>();

function findScrollContainer(el: HTMLElement | null): HTMLElement | null {
    let node = el?.parentElement ?? null;
    while (node) {
        const overflowY = getComputedStyle(node).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") return node;
        node = node.parentElement;
    }
    return null;
}

function deriveActiveView(pathname: string, basePath: string): ActiveView {
    if (pathname === `${basePath}/my-work`) return "myWork";
    if (pathname === `${basePath}/content`) return "content";
    if (pathname === `${basePath}/chat-search`) return "chatSearch";
    return "portals";
}

type DashboardLayoutProps = {
    basePath?: string;
};

export function DashboardLayout({basePath = ""}: DashboardLayoutProps) {
    const rootPath = basePath || "/";
    const myWorkPath = `${basePath}/my-work`;
    const {t} = useTranslation();
    const isMobile = useMediaQuery("(max-width: 48em)");
    const location = useLocation();
    const {mobileNavBarOpen, navbarExpanded, toggleNavBar} =
        useContext(AppContext);

    const activeView = deriveActiveView(location.pathname, basePath);
    const showToggle =
        (isMobile && !mobileNavBarOpen) || (!isMobile && !navbarExpanded);

    const rootRef = useRef<HTMLDivElement>(null);
    const topAreaRef = useRef<HTMLElement>(null);
    const maxTopAreaRef = useRef(0);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeSlug, setActiveSlug] = useState<string | null>(null);
    const activeSlugRef = useRef<string | null>(null);
    const scrollLockRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const {relevantGroupings, isLoading, isError} = usePortalData();
    // Play the sidebar entrance once, on a genuine first load, then drop the
    // animation classes. The sidebar persists across tabs and toggles
    // display:none on the My Work tab — a CSS animation re-runs whenever an
    // element returns from display:none, so leaving the class on would replay
    // it every time the user switches back to Portals.
    const navMountedLoading = useRef(isLoading);
    const [navEntranceDone, setNavEntranceDone] = useState(false);
    const animateNav =
        navMountedLoading.current && !isLoading && !navEntranceDone;
    useEffect(() => {
        if (!animateNav) return;
        const id = setTimeout(() => setNavEntranceDone(true), 1500);
        return () => clearTimeout(id);
    }, [animateNav]);

    // Collapse the hero/search area on scroll — but only when the page can
    // actually be scrolled past the collapse distance. On a short page (e.g. the
    // My Work tab) collapsing removes the height that made it scrollable, the
    // scroll snaps back to the top, the header expands again, and the two
    // oscillate (the flicker). A scrollability guard plus a hysteresis deadband
    // break that loop.
    useEffect(() => {
        if (isLoading || isError) return;
        const container = findScrollContainer(rootRef.current);
        if (!container) return;

        const COLLAPSE_ENTER = 64;
        const COLLAPSE_EXIT = 8;
        const MIN_SCROLLABLE = 140;

        const update = () => {
            const y = container.scrollTop;
            const maxScroll = container.scrollHeight - container.clientHeight;
            setIsCollapsed((prev) => {
                if (maxScroll <= MIN_SCROLLABLE) return false;
                if (!prev && y > COLLAPSE_ENTER) return true;
                if (prev && y < COLLAPSE_EXIT) return false;
                return prev;
            });
        };

        update();
        container.addEventListener("scroll", update, {passive: true});
        return () => container.removeEventListener("scroll", update);
    }, [isLoading, isError, activeView]);

    useEffect(() => {
        if (isLoading || isError) return;
        const el = topAreaRef.current;
        const root = rootRef.current;
        if (!el || !root) return;
        const observer = new ResizeObserver(([entry]) => {
            const height = entry.contentRect.height;
            root.style.setProperty("--top-area-height", `${height}px`);
            maxTopAreaRef.current = Math.max(maxTopAreaRef.current, height);
            root.style.setProperty(
                "--top-area-collapse",
                `${maxTopAreaRef.current - height}px`
            );
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [isLoading, isError]);

    // Scrollspy: track which grouping section is in view and highlight it in the
    // sidebar (active state lives in React, no longer written to the URL).
    useEffect(() => {
        if (isLoading || isError) return;
        if (activeView !== "portals") return;

        const sections = Array.from(
            document.querySelectorAll<HTMLElement>("[data-grouping-slug]")
        );
        if (sections.length === 0) return;

        const container = findScrollContainer(rootRef.current);

        // Active = the last section whose top has scrolled past an activation
        // line just below the sticky header. Each section takes its turn in
        // order as its top crosses the line. A short last section's top may
        // never reach the line, so a bottom guard elects it once the scroll has
        // actually bottomed out.
        const ACTIVATION_OFFSET = 150;
        const updateActive = () => {
            if (scrollLockRef.current) return;

            const lineY =
                (container ? container.getBoundingClientRect().top : 0) +
                ACTIVATION_OFFSET;

            let chosen: HTMLElement = sections[0];
            for (const el of sections) {
                if (el.getBoundingClientRect().top <= lineY) chosen = el;
                else break;
            }

            // Bottom guard: at the very end of the scroll the last section's top
            // may sit below the line forever — elect it so it isn't skipped.
            if (
                container &&
                container.scrollTop + container.clientHeight >=
                    container.scrollHeight - 2
            ) {
                chosen = sections[sections.length - 1];
            }

            const slug = chosen.getAttribute("data-grouping-slug");
            if (slug && slug !== activeSlugRef.current) {
                activeSlugRef.current = slug;
                setActiveSlug(slug);
            }
        };

        // Scroll is the primary driver (rAF-throttled).
        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                updateActive();
            });
        };
        container?.addEventListener("scroll", onScroll, {passive: true});

        // Recompute on non-scroll layout shifts too (e.g. content loading in).
        const observer = new IntersectionObserver(() => updateActive(), {
            threshold: 0,
        });
        sections.forEach((s) => observer.observe(s));

        updateActive();

        return () => {
            container?.removeEventListener("scroll", onScroll);
            observer.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [isLoading, isError, activeView, relevantGroupings.length]);

    // Honor an inbound URL hash on first load (after sections have rendered)
    useEffect(() => {
        if (isLoading || isError) return;
        if (activeView !== "portals") return;
        if (hasHonoredInboundHash) return;
        hasHonoredInboundHash = true;
        const hash = window.location.hash.slice(1);
        if (!hash) return;
        const el = document.getElementById(hash);
        if (!el) return;
        requestAnimationFrame(() => {
            el.scrollIntoView({behavior: "auto", block: "start"});
        });
    }, [isLoading, isError, activeView, relevantGroupings.length]);

    // Remember and restore the scroll container's position per route so
    // back-navigation lands where the user left off (the inner container is not
    // covered by the browser's native scroll restoration).
    useEffect(() => {
        if (isLoading || isError) return;
        const container = findScrollContainer(rootRef.current);
        if (!container) return;
        const key = location.pathname;

        const saved = scrollPositions.get(key);
        if (saved != null) {
            requestAnimationFrame(() => {
                container.scrollTop = saved;
            });
        }

        const onScroll = () => scrollPositions.set(key, container.scrollTop);
        container.addEventListener("scroll", onScroll, {passive: true});
        return () => container.removeEventListener("scroll", onScroll);
    }, [isLoading, isError, location.pathname]);

    const handleSidebarClick = (slug: string) => {
        const el = document.getElementById(`grouping-${slug}`);
        if (!el) return;

        if (scrollLockRef.current) clearTimeout(scrollLockRef.current);
        scrollLockRef.current = setTimeout(() => {
            scrollLockRef.current = null;
        }, 800);

        el.scrollIntoView({behavior: "smooth", block: "start"});
        activeSlugRef.current = slug;
        setActiveSlug(slug);
    };

    if (isError) {
        return (
            <Stack p="md">
                <Alert color="red" title="Error">
                    Failed to load dashboard data.
                </Alert>
            </Stack>
        );
    }

    const mobileGroupings = relevantGroupings.filter(
        (g): g is PortalGrouping & {name: string} => g.name !== undefined
    );
    const groupingSelectData = mobileGroupings.map((g) => ({
        value: groupingSlug(g.name, g.id),
        label: g.name,
    }));
    const mobileGroupingValue =
        activeView === "portals" ? (activeSlug ?? "") : "";

    return (
        <Stack
            align="center"
            w={"100%"}
            c={"dark.7"}
            gap="md"
            className={classes.root}
            ref={rootRef}
        >
            <header
                ref={topAreaRef}
                className={classes.stickyTop}
                data-collapsed={isCollapsed ? "true" : undefined}
                data-has-toggle={showToggle ? "true" : undefined}
            >
                {showToggle && (
                    <div className={classes.stickyToggle}>
                        <Tooltip label={t("common.openSidebar")}>
                            <ActionIcon
                                size="lg"
                                radius="xl"
                                variant="subtle"
                                color="gneis"
                                aria-label={t("common.openSidebar")}
                                onClick={toggleNavBar}
                                className={classes.toggleButton}
                            >
                                <IconSidebar width={20} />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                )}
                <DashboardSearchSection compact={isCollapsed} />

                <div className={classes.tabRow}>
                    <div className={classes.tabs}>
                        <UnstyledButton
                            component={Link}
                            to={rootPath}
                            className={classes.tab}
                            data-active={activeView === "portals"}
                        >
                            {t("Widget.internal.portal.title")}
                        </UnstyledButton>
                        <UnstyledButton
                            component={Link}
                            to={myWorkPath}
                            className={classes.tab}
                            data-active={activeView === "myWork"}
                        >
                            {t("Widget.internal.portal.myWork")}
                        </UnstyledButton>
                    </div>

                    {isMobile &&
                        activeView === "portals" &&
                        groupingSelectData.length > 0 && (
                            <Select
                                value={mobileGroupingValue}
                                data={groupingSelectData}
                                allowDeselect={false}
                                className={classes.mobileSelect}
                                classNames={{
                                    input: classes.mobileSelectInput,
                                    dropdown: classes.mobileSelectDropdown,
                                    option: classes.mobileSelectOption,
                                }}
                                rightSection={
                                    <IconChevronDown height={18} width={18} />
                                }
                                onChange={(value) => {
                                    if (value) handleSidebarClick(value);
                                }}
                            />
                        )}
                </div>
            </header>

            <Group
                align="stretch"
                wrap={isMobile ? "wrap" : "nowrap"}
                w="100%"
                className={classes.layout}
            >
                {!isMobile && (
                    <Stack
                        miw={200}
                        maw={250}
                        gap={0}
                        className={classes.sidebar}
                        w="100%"
                        style={{
                            display:
                                activeView === "portals" ? undefined : "none",
                        }}
                    >
                        {!isLoading && (
                            <>
                                {relevantGroupings.map((g, i) => {
                                    if (!g.name) return null;
                                    const slug = groupingSlug(g.name, g.id);
                                    const isGroupingActive =
                                        activeView === "portals" &&
                                        activeSlug === slug;
                                    return (
                                        <div
                                            key={g.id}
                                            className={
                                                animateNav
                                                    ? classes.navItemAnimate
                                                    : undefined
                                            }
                                            style={
                                                animateNav
                                                    ? {
                                                          animationDelay: `${i * 35}ms`,
                                                      }
                                                    : undefined
                                            }
                                        >
                                            <NavItem
                                                onClick={() =>
                                                    handleSidebarClick(slug)
                                                }
                                                active={isGroupingActive}
                                                rightSection={
                                                    <IconChevronRight />
                                                }
                                            >
                                                {g.name}
                                            </NavItem>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </Stack>
                )}
                <div className={classes.contentArea}>
                    <Outlet />
                </div>
            </Group>
            <div aria-hidden className={classes.scrollCompensation} />
        </Stack>
    );
}
